import { assert } from '../../../utils'

import { Capability, ConnectwareError, ConnectwareErrorType, type RuleEngineEndpoint, Translation } from '../../../domain'

import type { RuleEngineService, TranslationService } from '../../../application'

import { FetchConnectwareHTTPService } from '../Base'
import { isResource, mapResourceName, mapSearch, TopicsMapper } from '../mappers'

export class ConnectwareHTTPRuleEngineService extends FetchConnectwareHTTPService implements Pick<RuleEngineService, 'fetchEndpoints'> {
    private readonly translationService: TranslationService
    private readonly engineOptions: Readonly<{ searchMinimum: number, maxPageSize: number }>

    constructor (
        ...[baseOptions, translationService, engineOptions]: [
            ...ConstructorParameters<typeof FetchConnectwareHTTPService>,
            ConnectwareHTTPRuleEngineService['translationService'],
            ConnectwareHTTPRuleEngineService['engineOptions']
        ]
    ) {
        super(baseOptions)
        this.translationService = translationService
        this.engineOptions = engineOptions
    }

    fetchEndpoints (search: string): Promise<readonly RuleEngineEndpoint[]> {
        if (search && search.length < this.engineOptions.searchMinimum) {
            return Promise.reject(
                new ConnectwareError(
                    ConnectwareErrorType.BAD_REQUEST,
                    this.translationService.translate(Translation.SEARCH_THRESHOLD_EXPLANATION, { threshold: this.engineOptions.searchMinimum })
                )
            )
        }

        return this.request<'/api/v2/resources', 'GET', readonly RuleEngineEndpoint[]>({
            capability: Capability.RULE_ENGINE_RESOURCES,
            path: '/api/v2/resources',
            method: 'GET',
            authenticate: true,
            queryParams: {
                ...mapSearch(search),

                categories: ['Endpoint'],

                orderBy: 'resourceName',
                order: 'ASC',

                /**
                 * We don't want inactive endpoints to be listed
                 */
                onlyFromEnabledServices: 'true',

                /**
                 * Reasonable amount of items to find for an autocomplete search
                 */
                itemsPerPage: `${this.engineOptions.maxPageSize}`,
            },
            handlers: {
                200: (response) =>
                    response.getJson().then(({ data }) =>
                        data.map<RuleEngineEndpoint>((resource) => {
                            assert(
                                isResource(resource, 'Endpoint'),
                                new ConnectwareError(ConnectwareErrorType.MAPPING_ERROR, 'Unexpected non endpoint resource category', {
                                    category: resource.category,
                                })
                            )

                            return {
                                id: resource.resourceId,
                                name: mapResourceName(resource.resourceId),
                                connection: resource.parentResourceId,
                                service: resource.serviceId,
                                topics: TopicsMapper.create(resource.category)
                                    .withOperation(resource.resourceConfig.args.operation)
                                    .mapAll(resource.resourceConfig.args.topic),
                            }
                        })
                    ),
                400: () => new ConnectwareError(ConnectwareErrorType.BAD_REQUEST, 'Bad request detected while searching endpoints'),
                500: () => new ConnectwareError(ConnectwareErrorType.SERVER_ERROR, 'Could not fetch searched endpoints'),
            },
        })
    }
}
